import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Separator } from '../../components/ui/separator';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Share2, Facebook, Instagram, MessageSquare, Search, Filter,
  Send, ThumbsUp, MessageCircle, Heart, Repeat2, Eye, Users,
  Link2, ExternalLink, MoreVertical, Settings, CheckCircle2,
  Clock, AlertCircle, Image, Smile, Paperclip, Phone, Mail,
  Briefcase, Tag, ChevronRight, User, Globe, Building2, Hash,
  Calendar, TrendingUp, ArrowRight, Plus, RefreshCw, Bell,
  UserPlus, FileText, X, Check, Loader2
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../../components/ui/dropdown-menu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Platform configurations
const PLATFORMS = {
  facebook: {
    name: 'Facebook',
    icon: Facebook,
    color: 'bg-blue-600',
    textColor: 'text-blue-600',
    borderColor: 'border-blue-600',
    connected: true, // For Phase 1
  },
  instagram: {
    name: 'Instagram',
    icon: Instagram,
    color: 'bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500',
    textColor: 'text-pink-500',
    borderColor: 'border-pink-500',
    connected: false,
    comingSoon: true,
  },
  google: {
    name: 'Google Business',
    icon: Building2,
    color: 'bg-green-600',
    textColor: 'text-green-600',
    borderColor: 'border-green-600',
    connected: false,
    comingSoon: true,
  },
  twitter: {
    name: 'X / Twitter',
    icon: Hash,
    color: 'bg-slate-900',
    textColor: 'text-slate-900 dark:text-white',
    borderColor: 'border-slate-900',
    connected: false,
    comingSoon: true,
  },
  linkedin: {
    name: 'LinkedIn',
    icon: Globe,
    color: 'bg-blue-700',
    textColor: 'text-blue-700',
    borderColor: 'border-blue-700',
    connected: false,
    comingSoon: true,
  },
};

// Mock Facebook data
const MOCK_FB_CONVERSATIONS = [
  {
    id: 'fb-conv-1',
    platform: 'facebook',
    type: 'messenger',
    user: {
      id: 'fb-user-1',
      name: 'Sarah Johnson',
      profileUrl: 'https://facebook.com/sarahjohnson',
      avatarUrl: null,
    },
    lastMessage: 'Hi! I saw your ad for exterior painting. Can you give me a quote for my 2500 sq ft home?',
    lastMessageAt: new Date(Date.now() - 1000 * 60 * 15).toISOString(),
    unreadCount: 2,
    assignedTo: null,
    status: 'open',
    linkedCustomerId: null,
    linkedJobId: null,
  },
  {
    id: 'fb-conv-2',
    platform: 'facebook',
    type: 'messenger',
    user: {
      id: 'fb-user-2',
      name: 'Mike Thompson',
      profileUrl: 'https://facebook.com/mikethompson',
      avatarUrl: null,
    },
    lastMessage: 'Thanks for the estimate! When can you start?',
    lastMessageAt: new Date(Date.now() - 1000 * 60 * 45).toISOString(),
    unreadCount: 0,
    assignedTo: 'Ryan March',
    status: 'open',
    linkedCustomerId: 'cust-123',
    linkedJobId: 'job-456',
  },
  {
    id: 'fb-conv-3',
    platform: 'facebook',
    type: 'comment',
    user: {
      id: 'fb-user-3',
      name: 'Jennifer Williams',
      profileUrl: 'https://facebook.com/jenniferwilliams',
      avatarUrl: null,
    },
    lastMessage: 'Beautiful work! How long does the coating last?',
    lastMessageAt: new Date(Date.now() - 1000 * 60 * 120).toISOString(),
    unreadCount: 1,
    assignedTo: null,
    status: 'open',
    linkedCustomerId: null,
    linkedJobId: null,
    postId: 'post-1',
    postPreview: 'Check out this stunning transformation...',
  },
  {
    id: 'fb-conv-4',
    platform: 'facebook',
    type: 'messenger',
    user: {
      id: 'fb-user-4',
      name: 'David Chen',
      profileUrl: 'https://facebook.com/davidchen',
      avatarUrl: null,
    },
    lastMessage: 'Do you offer financing options?',
    lastMessageAt: new Date(Date.now() - 1000 * 60 * 60 * 3).toISOString(),
    unreadCount: 0,
    assignedTo: null,
    status: 'waiting',
    linkedCustomerId: null,
    linkedJobId: null,
  },
  {
    id: 'fb-conv-5',
    platform: 'facebook',
    type: 'comment',
    user: {
      id: 'fb-user-5',
      name: 'Amanda Foster',
      profileUrl: 'https://facebook.com/amandafoster',
      avatarUrl: null,
    },
    lastMessage: 'Great service! Ryan and his team were professional.',
    lastMessageAt: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString(),
    unreadCount: 0,
    assignedTo: null,
    status: 'closed',
    linkedCustomerId: 'cust-789',
    linkedJobId: 'job-012',
    postId: 'post-2',
    postPreview: 'Another happy customer in Jacksonville!',
  },
];

const MOCK_FB_MESSAGES = {
  'fb-conv-1': [
    {
      id: 'msg-1',
      direction: 'inbound',
      content: 'Hi! I saw your ad for exterior painting. Can you give me a quote for my 2500 sq ft home?',
      timestamp: new Date(Date.now() - 1000 * 60 * 20).toISOString(),
      senderName: 'Sarah Johnson',
      read: false,
    },
    {
      id: 'msg-2',
      direction: 'inbound',
      content: 'I\'m located in Jacksonville, FL - Riverside area',
      timestamp: new Date(Date.now() - 1000 * 60 * 15).toISOString(),
      senderName: 'Sarah Johnson',
      read: false,
    },
  ],
  'fb-conv-2': [
    {
      id: 'msg-3',
      direction: 'inbound',
      content: 'Hello, I\'m interested in getting a quote for my home exterior',
      timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(),
      senderName: 'Mike Thompson',
      read: true,
    },
    {
      id: 'msg-4',
      direction: 'outbound',
      content: 'Hi Mike! Thanks for reaching out. I\'d be happy to provide a free estimate. Can you share your address and some photos of your home?',
      timestamp: new Date(Date.now() - 1000 * 60 * 60 * 1.5).toISOString(),
      senderName: 'Rhino Shield Florida',
      read: true,
    },
    {
      id: 'msg-5',
      direction: 'inbound',
      content: 'Sure! I\'m at 456 Oak Street, Jacksonville. Here\'s a photo of the front.',
      timestamp: new Date(Date.now() - 1000 * 60 * 60).toISOString(),
      senderName: 'Mike Thompson',
      read: true,
      hasImage: true,
    },
    {
      id: 'msg-6',
      direction: 'outbound',
      content: 'Perfect! Based on the photos, I estimate around $8,500 for a full exterior coating. This includes our 25-year warranty. Would you like to schedule an in-person assessment?',
      timestamp: new Date(Date.now() - 1000 * 60 * 50).toISOString(),
      senderName: 'Rhino Shield Florida',
      read: true,
    },
    {
      id: 'msg-7',
      direction: 'inbound',
      content: 'Thanks for the estimate! When can you start?',
      timestamp: new Date(Date.now() - 1000 * 60 * 45).toISOString(),
      senderName: 'Mike Thompson',
      read: true,
    },
  ],
};

const MOCK_FB_POSTS = [
  {
    id: 'post-1',
    content: 'Check out this stunning transformation! 🏠✨ Another beautiful home in Ponte Vedra Beach protected with our premium ceramic coating. This exterior will stay vibrant for 25+ years!\n\n#RhinoShield #HomeImprovement #JacksonvilleFL',
    imageUrl: 'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=600',
    publishedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2).toISOString(),
    engagement: {
      likes: 156,
      comments: 23,
      shares: 12,
    },
    comments: [
      {
        id: 'comment-1',
        user: { name: 'Jennifer Williams', profileUrl: '#' },
        content: 'Beautiful work! How long does the coating last?',
        timestamp: new Date(Date.now() - 1000 * 60 * 120).toISOString(),
        likes: 3,
        replies: [
          {
            id: 'reply-1',
            user: { name: 'Rhino Shield Florida', profileUrl: '#', isPage: true },
            content: 'Thank you Jennifer! Our ceramic coating comes with a 25-year warranty and typically lasts even longer. Would you like more information?',
            timestamp: new Date(Date.now() - 1000 * 60 * 100).toISOString(),
            likes: 1,
          },
        ],
      },
      {
        id: 'comment-2',
        user: { name: 'Robert Martinez', profileUrl: '#' },
        content: 'Great job! The colors look amazing 👏',
        timestamp: new Date(Date.now() - 1000 * 60 * 60 * 12).toISOString(),
        likes: 5,
        replies: [],
      },
    ],
  },
  {
    id: 'post-2',
    content: 'Another happy customer in Jacksonville! 🎉 The Wilson family chose Rhino Shield to protect their beautiful colonial home. Thank you for trusting us with your investment!\n\n#CustomerAppreciation #HomeProtection',
    imageUrl: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=600',
    publishedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 5).toISOString(),
    engagement: {
      likes: 234,
      comments: 31,
      shares: 18,
    },
    comments: [
      {
        id: 'comment-3',
        user: { name: 'Amanda Foster', profileUrl: '#' },
        content: 'Great service! Ryan and his team were professional and the results exceeded our expectations.',
        timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString(),
        likes: 12,
        replies: [],
      },
    ],
  },
];

// Connect Account Modal Component
const ConnectAccountModal = ({ open, onClose, platform }) => {
  const platformConfig = PLATFORMS[platform] || PLATFORMS.facebook;
  const PlatformIcon = platformConfig.icon;
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className={`p-2 rounded-lg ${platformConfig.color}`}>
              <PlatformIcon className="h-5 w-5 text-white" />
            </div>
            Connect {platformConfig.name}
          </DialogTitle>
          <DialogDescription>
            Connect your {platformConfig.name} account to manage customer communications.
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4 space-y-4">
          <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4 space-y-3">
            <h4 className="font-medium text-sm">Connection Steps:</h4>
            <ol className="text-sm text-muted-foreground space-y-2">
              <li className="flex items-start gap-2">
                <span className="bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 rounded-full w-5 h-5 flex items-center justify-center text-xs shrink-0">1</span>
                <span>Click &quot;Connect with {platformConfig.name}&quot; below</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 rounded-full w-5 h-5 flex items-center justify-center text-xs shrink-0">2</span>
                <span>Log in to your {platformConfig.name} account</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 rounded-full w-5 h-5 flex items-center justify-center text-xs shrink-0">3</span>
                <span>Select the business page you want to connect</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 rounded-full w-5 h-5 flex items-center justify-center text-xs shrink-0">4</span>
                <span>Grant required permissions for messaging and comments</span>
              </li>
            </ol>
          </div>
          
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
            <p className="text-sm text-amber-700 dark:text-amber-400">
              <strong>Note:</strong> You must be an admin of the Facebook Page to connect it.
            </p>
          </div>
          
          <div className="text-xs text-muted-foreground">
            <strong>Permissions requested:</strong>
            <ul className="mt-1 space-y-1">
              <li>• Read and respond to messages</li>
              <li>• Read and reply to comments</li>
              <li>• View page insights</li>
            </ul>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button 
            className={platformConfig.color}
            onClick={() => {
              toast.info('Facebook OAuth integration coming soon! This is a demo.');
              onClose();
            }}
          >
            <PlatformIcon className="h-4 w-4 mr-2" />
            Connect with {platformConfig.name}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Conversation Item Component
const ConversationItem = ({ conversation, isSelected, onClick }) => {
  const isUnread = conversation.unreadCount > 0;
  const timeAgo = getTimeAgo(conversation.lastMessageAt);
  
  return (
    <div
      onClick={onClick}
      className={`p-3 cursor-pointer border-b transition-colors ${
        isSelected 
          ? 'bg-blue-50 dark:bg-blue-900/20 border-l-2 border-l-blue-500' 
          : 'hover:bg-slate-50 dark:hover:bg-slate-800'
      } ${isUnread ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''}`}
    >
      <div className="flex items-start gap-3">
        <div className="relative">
          <Avatar className="h-10 w-10">
            <AvatarImage src={conversation.user.avatarUrl} />
            <AvatarFallback className="bg-blue-100 text-blue-600">
              {conversation.user.name.split(' ').map(n => n[0]).join('')}
            </AvatarFallback>
          </Avatar>
          <div className="absolute -bottom-1 -right-1 bg-white dark:bg-slate-900 rounded-full p-0.5">
            {conversation.type === 'messenger' ? (
              <MessageCircle className="h-3 w-3 text-blue-600" />
            ) : (
              <MessageSquare className="h-3 w-3 text-green-600" />
            )}
          </div>
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <span className={`font-medium text-sm truncate ${isUnread ? 'text-foreground' : 'text-muted-foreground'}`}>
              {conversation.user.name}
            </span>
            <span className="text-xs text-muted-foreground shrink-0">{timeAgo}</span>
          </div>
          
          <p className={`text-sm truncate ${isUnread ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>
            {conversation.lastMessage}
          </p>
          
          <div className="flex items-center gap-2 mt-1.5">
            <Badge variant="outline" className="text-xs py-0 h-5">
              {conversation.type === 'messenger' ? 'DM' : 'Comment'}
            </Badge>
            {conversation.assignedTo && (
              <Badge variant="secondary" className="text-xs py-0 h-5">
                <User className="h-3 w-3 mr-1" />
                {conversation.assignedTo.split(' ')[0]}
              </Badge>
            )}
            {conversation.linkedJobId && (
              <Badge className="text-xs py-0 h-5 bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">
                <Briefcase className="h-3 w-3 mr-1" />
                Job
              </Badge>
            )}
          </div>
        </div>
        
        {isUnread && (
          <div className="bg-blue-600 text-white text-xs font-medium rounded-full h-5 min-w-[20px] flex items-center justify-center px-1.5">
            {conversation.unreadCount}
          </div>
        )}
      </div>
    </div>
  );
};

// Message Bubble Component
const MessageBubble = ({ message, isOutbound }) => {
  return (
    <div className={`flex ${isOutbound ? 'justify-end' : 'justify-start'} mb-3`}>
      <div className={`max-w-[70%] ${isOutbound ? 'order-2' : ''}`}>
        <div className={`rounded-2xl px-4 py-2 ${
          isOutbound 
            ? 'bg-blue-600 text-white rounded-br-md' 
            : 'bg-slate-100 dark:bg-slate-800 rounded-bl-md'
        }`}>
          {message.hasImage && (
            <div className="mb-2 rounded-lg overflow-hidden">
              <div className="bg-slate-200 dark:bg-slate-700 h-32 flex items-center justify-center">
                <Image className="h-8 w-8 text-muted-foreground" />
              </div>
            </div>
          )}
          <p className="text-sm">{message.content}</p>
        </div>
        <div className={`flex items-center gap-2 mt-1 text-xs text-muted-foreground ${isOutbound ? 'justify-end' : ''}`}>
          <span>{formatTime(message.timestamp)}</span>
          {isOutbound && message.read && <CheckCircle2 className="h-3 w-3 text-blue-500" />}
        </div>
      </div>
    </div>
  );
};

// Post Card Component
const PostCard = ({ post, onViewComments }) => {
  return (
    <Card className="mb-4">
      <CardContent className="p-4">
        <div className="flex items-start gap-3 mb-3">
          <Avatar className="h-10 w-10">
            <AvatarFallback className="bg-blue-600 text-white">RS</AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <div className="font-medium">Rhino Shield Florida</div>
            <div className="text-xs text-muted-foreground flex items-center gap-1">
              <Globe className="h-3 w-3" />
              {formatDate(post.publishedAt)}
            </div>
          </div>
        </div>
        
        <p className="text-sm whitespace-pre-wrap mb-3">{post.content}</p>
        
        {post.imageUrl && (
          <div className="rounded-lg overflow-hidden mb-3">
            <img src={post.imageUrl} alt="Post" className="w-full h-48 object-cover" />
          </div>
        )}
        
        <div className="flex items-center justify-between py-2 border-y">
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <ThumbsUp className="h-4 w-4" />
              {post.engagement.likes}
            </span>
            <span className="flex items-center gap-1">
              <MessageCircle className="h-4 w-4" />
              {post.engagement.comments}
            </span>
            <span className="flex items-center gap-1">
              <Share2 className="h-4 w-4" />
              {post.engagement.shares}
            </span>
          </div>
          <Button variant="ghost" size="sm" onClick={() => onViewComments(post)}>
            View Comments
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Helper functions
function getTimeAgo(dateString) {
  const now = new Date();
  const date = new Date(dateString);
  const seconds = Math.floor((now - date) / 1000);
  
  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d`;
  return date.toLocaleDateString();
}

function formatTime(dateString) {
  return new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatDate(dateString) {
  return new Date(dateString).toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// Main Component
const SocialMedia = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // State
  const [activePlatform, setActivePlatform] = useState('facebook');
  const [activeView, setActiveView] = useState('inbox'); // inbox | posts
  const [conversations, setConversations] = useState(MOCK_FB_CONVERSATIONS);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [posts, setPosts] = useState(MOCK_FB_POSTS);
  const [selectedPost, setSelectedPost] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [newReply, setNewReply] = useState('');
  
  // Filters
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modals
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [showLinkCustomerModal, setShowLinkCustomerModal] = useState(false);
  
  // Handle conversation selection with messages
  const handleSelectConversation = useCallback((conv) => {
    setSelectedConversation(conv);
    setSelectedPost(null);
    setMessages(MOCK_FB_MESSAGES[conv.id] || []);
  }, []);
  
  // Filter conversations
  const filteredConversations = conversations.filter(conv => {
    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      if (!conv.user.name.toLowerCase().includes(search) && 
          !conv.lastMessage.toLowerCase().includes(search)) {
        return false;
      }
    }
    
    // Status filter
    if (filter === 'unread' && conv.unreadCount === 0) return false;
    if (filter === 'assigned' && !conv.assignedTo) return false;
    if (filter === 'unassigned' && conv.assignedTo) return false;
    if (filter === 'messenger' && conv.type !== 'messenger') return false;
    if (filter === 'comments' && conv.type !== 'comment') return false;
    
    return true;
  });
  
  // Handle send message
  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;
    
    setSending(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const newMsg = {
      id: `msg-${Date.now()}`,
      direction: 'outbound',
      content: newMessage,
      timestamp: new Date().toISOString(),
      senderName: 'Rhino Shield Florida',
      read: false,
    };
    
    setMessages(prev => [...prev, newMsg]);
    setNewMessage('');
    setSending(false);
    
    toast.success('Message sent to Facebook');
    
    // Update conversation
    setConversations(prev => prev.map(c => 
      c.id === selectedConversation.id
        ? { ...c, lastMessage: newMessage, lastMessageAt: new Date().toISOString() }
        : c
    ));
  };
  
  // Platform stats
  const stats = {
    unread: conversations.filter(c => c.unreadCount > 0).length,
    open: conversations.filter(c => c.status === 'open').length,
    waiting: conversations.filter(c => c.status === 'waiting').length,
  };
  
  // Render platform placeholder
  const renderPlatformPlaceholder = (platform) => {
    const config = PLATFORMS[platform];
    const PlatformIcon = config.icon;
    
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className={`mx-auto w-20 h-20 rounded-2xl ${config.color} flex items-center justify-center mb-6`}>
            <PlatformIcon className="h-10 w-10 text-white" />
          </div>
          <h2 className="text-2xl font-bold mb-2">{config.name}</h2>
          <p className="text-muted-foreground mb-6">
            {config.name} integration is coming soon. Connect your account to manage customer conversations directly from Rhino-1.
          </p>
          <Badge variant="secondary" className="text-sm py-1 px-3">
            <Clock className="h-4 w-4 mr-2" />
            Coming Soon
          </Badge>
        </div>
      </div>
    );
  };
  
  return (
    <Layout>
      <div className="h-[calc(100vh-140px)] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-3">
              <Share2 className="h-7 w-7 text-primary" />
              Social Media Communications
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              Unified customer conversations across social platforms
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Connection Status */}
            <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span className="text-sm text-green-700 dark:text-green-400">Facebook Connected</span>
            </div>
            
            <Button variant="outline" onClick={() => setShowConnectModal(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Connect Account
            </Button>
            
            <Button variant="ghost" size="icon">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>
        
        {/* Platform Tabs */}
        <Tabs value={activePlatform} onValueChange={setActivePlatform} className="flex-1 flex flex-col min-h-0">
          <TabsList className="w-full justify-start gap-1 bg-slate-100 dark:bg-slate-800 p-1 h-12">
            {Object.entries(PLATFORMS).map(([key, platform]) => {
              const PlatformIcon = platform.icon;
              return (
                <TabsTrigger 
                  key={key} 
                  value={key}
                  className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-900 gap-2 px-4"
                >
                  <PlatformIcon className={`h-4 w-4 ${platform.textColor}`} />
                  {platform.name}
                  {platform.comingSoon && (
                    <Badge variant="secondary" className="text-[10px] py-0 px-1.5 ml-1">Soon</Badge>
                  )}
                  {key === 'facebook' && stats.unread > 0 && (
                    <Badge className="bg-red-500 text-white text-[10px] py-0 px-1.5 ml-1">{stats.unread}</Badge>
                  )}
                </TabsTrigger>
              );
            })}
          </TabsList>
          
          {/* Facebook Content */}
          <TabsContent value="facebook" className="flex-1 mt-4 min-h-0">
            <div className="h-full flex gap-4">
              {/* Left Panel - Conversation List */}
              <Card className="w-[380px] flex flex-col">
                <CardHeader className="pb-2 space-y-3">
                  {/* View Toggle */}
                  <div className="flex gap-1 bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
                    <Button
                      variant={activeView === 'inbox' ? 'default' : 'ghost'}
                      size="sm"
                      className="flex-1"
                      onClick={() => setActiveView('inbox')}
                    >
                      <MessageCircle className="h-4 w-4 mr-2" />
                      Inbox
                    </Button>
                    <Button
                      variant={activeView === 'posts' ? 'default' : 'ghost'}
                      size="sm"
                      className="flex-1"
                      onClick={() => setActiveView('posts')}
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      Posts
                    </Button>
                  </div>
                  
                  {activeView === 'inbox' && (
                    <>
                      {/* Search */}
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="Search conversations..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="pl-9"
                        />
                      </div>
                      
                      {/* Filters */}
                      <div className="flex flex-wrap gap-1">
                        {[
                          { value: 'all', label: 'All' },
                          { value: 'unread', label: 'Unread' },
                          { value: 'assigned', label: 'Assigned' },
                          { value: 'unassigned', label: 'Unassigned' },
                          { value: 'messenger', label: 'DMs' },
                          { value: 'comments', label: 'Comments' },
                        ].map(f => (
                          <Badge
                            key={f.value}
                            variant={filter === f.value ? 'default' : 'outline'}
                            className="cursor-pointer"
                            onClick={() => setFilter(f.value)}
                          >
                            {f.label}
                          </Badge>
                        ))}
                      </div>
                    </>
                  )}
                </CardHeader>
                
                <CardContent className="flex-1 overflow-hidden p-0">
                  <ScrollArea className="h-full">
                    {activeView === 'inbox' ? (
                      filteredConversations.length > 0 ? (
                        filteredConversations.map(conv => (
                          <ConversationItem
                            key={conv.id}
                            conversation={conv}
                            isSelected={selectedConversation?.id === conv.id}
                            onClick={() => handleSelectConversation(conv)}
                          />
                        ))
                      ) : (
                        <div className="p-8 text-center text-muted-foreground">
                          <MessageCircle className="h-12 w-12 mx-auto mb-3 opacity-30" />
                          <p>No conversations found</p>
                        </div>
                      )
                    ) : (
                      posts.map(post => (
                        <PostCard
                          key={post.id}
                          post={post}
                          onViewComments={(p) => {
                            setSelectedPost(p);
                            setSelectedConversation(null);
                          }}
                        />
                      ))
                    )}
                  </ScrollArea>
                </CardContent>
              </Card>
              
              {/* Center Panel - Conversation/Post View */}
              <Card className="flex-1 flex flex-col">
                {selectedConversation ? (
                  <>
                    {/* Conversation Header */}
                    <CardHeader className="border-b pb-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-10 w-10">
                            <AvatarFallback className="bg-blue-100 text-blue-600">
                              {selectedConversation.user.name.split(' ').map(n => n[0]).join('')}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <h3 className="font-semibold flex items-center gap-2">
                              {selectedConversation.user.name}
                              <Badge variant="outline" className="text-xs">
                                <Facebook className="h-3 w-3 mr-1 text-blue-600" />
                                {selectedConversation.type === 'messenger' ? 'Messenger' : 'Comment'}
                              </Badge>
                            </h3>
                            <a 
                              href={selectedConversation.user.profileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-muted-foreground hover:text-primary flex items-center gap-1"
                            >
                              View Profile <ExternalLink className="h-3 w-3" />
                            </a>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Badge variant={selectedConversation.status === 'open' ? 'default' : 'secondary'}>
                            {selectedConversation.status}
                          </Badge>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => toast.info('Assign feature coming soon')}>
                                <User className="h-4 w-4 mr-2" />
                                Assign to Agent
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => setShowLinkCustomerModal(true)}>
                                <UserPlus className="h-4 w-4 mr-2" />
                                Link to Customer
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => toast.info('Create job feature coming soon')}>
                                <Briefcase className="h-4 w-4 mr-2" />
                                Create Job
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => toast.info('Mark as closed')}>
                                <CheckCircle2 className="h-4 w-4 mr-2" />
                                Mark as Closed
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                      
                      {/* Post context if comment */}
                      {selectedConversation.type === 'comment' && selectedConversation.postPreview && (
                        <div className="mt-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                          <p className="text-xs text-muted-foreground mb-1">Comment on post:</p>
                          <p className="text-sm truncate">{selectedConversation.postPreview}</p>
                        </div>
                      )}
                    </CardHeader>
                    
                    {/* Messages */}
                    <CardContent className="flex-1 overflow-hidden p-0">
                      <ScrollArea className="h-full p-4">
                        {messages.map(msg => (
                          <MessageBubble
                            key={msg.id}
                            message={msg}
                            isOutbound={msg.direction === 'outbound'}
                          />
                        ))}
                      </ScrollArea>
                    </CardContent>
                    
                    {/* Message Input */}
                    <div className="p-4 border-t">
                      <div className="flex gap-2">
                        <div className="flex-1 relative">
                          <Textarea
                            placeholder={`Reply to ${selectedConversation.user.name}...`}
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            className="min-h-[60px] pr-20 resize-none"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleSendMessage();
                              }
                            }}
                          />
                          <div className="absolute right-2 bottom-2 flex items-center gap-1">
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <Smile className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <Paperclip className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        <Button 
                          onClick={handleSendMessage}
                          disabled={!newMessage.trim() || sending}
                          className="self-end"
                        >
                          {sending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Send className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">
                        Press Enter to send, Shift+Enter for new line
                      </p>
                    </div>
                  </>
                ) : selectedPost ? (
                  <>
                    {/* Post Header */}
                    <CardHeader className="border-b pb-3">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold">Post Comments</h3>
                        <Button variant="ghost" size="sm" onClick={() => setSelectedPost(null)}>
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                      <p className="text-sm text-muted-foreground line-clamp-2">{selectedPost.content}</p>
                    </CardHeader>
                    
                    {/* Comments */}
                    <CardContent className="flex-1 overflow-hidden p-0">
                      <ScrollArea className="h-full p-4">
                        {selectedPost.comments.map(comment => (
                          <div key={comment.id} className="mb-4">
                            <div className="flex items-start gap-3">
                              <Avatar className="h-8 w-8">
                                <AvatarFallback className="text-xs">
                                  {comment.user.name.split(' ').map(n => n[0]).join('')}
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex-1">
                                <div className="bg-slate-100 dark:bg-slate-800 rounded-2xl rounded-tl-md px-4 py-2">
                                  <p className="font-medium text-sm">{comment.user.name}</p>
                                  <p className="text-sm">{comment.content}</p>
                                </div>
                                <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                                  <span>{getTimeAgo(comment.timestamp)}</span>
                                  <button className="hover:text-foreground">Like</button>
                                  <button className="hover:text-foreground">Reply</button>
                                  <span>{comment.likes} likes</span>
                                </div>
                                
                                {/* Replies */}
                                {comment.replies?.map(reply => (
                                  <div key={reply.id} className="ml-8 mt-3 flex items-start gap-3">
                                    <Avatar className="h-7 w-7">
                                      <AvatarFallback className={`text-xs ${reply.user.isPage ? 'bg-blue-100 text-blue-600' : ''}`}>
                                        {reply.user.name.split(' ').map(n => n[0]).join('')}
                                      </AvatarFallback>
                                    </Avatar>
                                    <div className="flex-1">
                                      <div className={`rounded-2xl rounded-tl-md px-4 py-2 ${
                                        reply.user.isPage ? 'bg-blue-50 dark:bg-blue-900/30' : 'bg-slate-100 dark:bg-slate-800'
                                      }`}>
                                        <p className="font-medium text-sm flex items-center gap-1">
                                          {reply.user.name}
                                          {reply.user.isPage && (
                                            <Badge variant="secondary" className="text-[10px] py-0 px-1">Page</Badge>
                                          )}
                                        </p>
                                        <p className="text-sm">{reply.content}</p>
                                      </div>
                                      <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                                        <span>{getTimeAgo(reply.timestamp)}</span>
                                        <span>{reply.likes} likes</span>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        ))}
                      </ScrollArea>
                    </CardContent>
                    
                    {/* Reply Input */}
                    <div className="p-4 border-t">
                      <div className="flex gap-2">
                        <Input
                          placeholder="Write a reply..."
                          value={newReply}
                          onChange={(e) => setNewReply(e.target.value)}
                        />
                        <Button onClick={() => {
                          if (newReply.trim()) {
                            toast.success('Reply posted to Facebook');
                            setNewReply('');
                          }
                        }}>
                          <Send className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="flex-1 flex items-center justify-center text-center p-8">
                    <div>
                      <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                        <Facebook className="h-10 w-10 text-blue-600" />
                      </div>
                      <h3 className="font-medium text-lg mb-2">Facebook Conversations</h3>
                      <p className="text-muted-foreground text-sm max-w-sm">
                        Select a conversation from the list to view and respond to messages, or browse posts to manage comments.
                      </p>
                    </div>
                  </div>
                )}
              </Card>
              
              {/* Right Panel - Context */}
              {selectedConversation && (
                <Card className="w-[320px] flex flex-col">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm">Customer Context</CardTitle>
                  </CardHeader>
                  <CardContent className="flex-1 overflow-auto space-y-4">
                    {/* Profile Info */}
                    <div className="text-center">
                      <Avatar className="h-16 w-16 mx-auto mb-2">
                        <AvatarFallback className="text-lg bg-blue-100 text-blue-600">
                          {selectedConversation.user.name.split(' ').map(n => n[0]).join('')}
                        </AvatarFallback>
                      </Avatar>
                      <h4 className="font-semibold">{selectedConversation.user.name}</h4>
                      <a 
                        href={selectedConversation.user.profileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:underline flex items-center justify-center gap-1"
                      >
                        <Facebook className="h-3 w-3" />
                        Facebook Profile
                      </a>
                    </div>
                    
                    <Separator />
                    
                    {/* Linked Records */}
                    <div className="space-y-3">
                      <h5 className="text-sm font-medium">Linked Records</h5>
                      
                      {selectedConversation.linkedCustomerId ? (
                        <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                          <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
                            <CheckCircle2 className="h-4 w-4" />
                            <span className="text-sm font-medium">Customer Linked</span>
                          </div>
                          <Button variant="link" size="sm" className="p-0 h-auto mt-1">
                            View Customer Record →
                          </Button>
                        </div>
                      ) : (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="w-full"
                          onClick={() => setShowLinkCustomerModal(true)}
                        >
                          <UserPlus className="h-4 w-4 mr-2" />
                          Link to Customer
                        </Button>
                      )}
                      
                      {selectedConversation.linkedJobId ? (
                        <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                          <div className="flex items-center gap-2 text-blue-700 dark:text-blue-400">
                            <Briefcase className="h-4 w-4" />
                            <span className="text-sm font-medium">Job Linked</span>
                          </div>
                          <Button variant="link" size="sm" className="p-0 h-auto mt-1">
                            View Job →
                          </Button>
                        </div>
                      ) : (
                        <Button variant="outline" size="sm" className="w-full">
                          <Briefcase className="h-4 w-4 mr-2" />
                          Create Job
                        </Button>
                      )}
                    </div>
                    
                    <Separator />
                    
                    {/* Interaction History */}
                    <div className="space-y-3">
                      <h5 className="text-sm font-medium">Interaction History</h5>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground flex items-center gap-2">
                            <MessageCircle className="h-4 w-4" />
                            Social Messages
                          </span>
                          <span className="font-medium">{messages.length}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground flex items-center gap-2">
                            <Phone className="h-4 w-4" />
                            Calls
                          </span>
                          <span className="font-medium">0</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground flex items-center gap-2">
                            <Mail className="h-4 w-4" />
                            Emails
                          </span>
                          <span className="font-medium">0</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground flex items-center gap-2">
                            <MessageSquare className="h-4 w-4" />
                            SMS
                          </span>
                          <span className="font-medium">0</span>
                        </div>
                      </div>
                    </div>
                    
                    <Separator />
                    
                    {/* Internal Notes */}
                    <div className="space-y-3">
                      <h5 className="text-sm font-medium">Internal Notes</h5>
                      <Textarea
                        placeholder="Add a note (not visible to customer)..."
                        className="min-h-[80px] text-sm"
                      />
                      <Button size="sm" variant="outline" className="w-full">
                        <Plus className="h-4 w-4 mr-2" />
                        Add Note
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
          
          {/* Other Platform Placeholders */}
          {Object.entries(PLATFORMS).filter(([key]) => key !== 'facebook').map(([key, platform]) => (
            <TabsContent key={key} value={key} className="flex-1 mt-4">
              {renderPlatformPlaceholder(key)}
            </TabsContent>
          ))}
        </Tabs>
      </div>
      
      {/* Connect Account Modal */}
      <ConnectAccountModal
        open={showConnectModal}
        onClose={() => setShowConnectModal(false)}
        platform={activePlatform}
      />
      
      {/* Link Customer Modal */}
      <Dialog open={showLinkCustomerModal} onOpenChange={setShowLinkCustomerModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Link to Customer</DialogTitle>
            <DialogDescription>
              Search for an existing customer or create a new one.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search customers..." className="pl-9" />
            </div>
            <div className="text-center py-6 text-muted-foreground">
              <Users className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm">Search for a customer above or create a new one</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLinkCustomerModal(false)}>
              Cancel
            </Button>
            <Button onClick={() => {
              toast.success('Customer linked successfully');
              setShowLinkCustomerModal(false);
            }}>
              <UserPlus className="h-4 w-4 mr-2" />
              Create New Customer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default SocialMedia;
